/**
 * O que falta numa etapa, comparado com a anterior.
 *
 * Cada etapa do workflow lê a anterior UMA vez: na primeira abertura, e só
 * quando ainda não tem nada gravado. Depois disso o caminho de volta some —
 * uma FIV salva com duas doadoras nunca mais olhava a OPU, mesmo que a OPU
 * tivesse dez. O botão "Importar da etapa anterior" refaz a pergunta, e estas
 * funções são a resposta: **o que falta**, nunca o que substituir.
 *
 * Elas devolvem só as linhas ausentes de propósito. Quem já está na etapa foi
 * digitado por alguém olhando o animal, e a etapa anterior não sabe mais que
 * essa pessoa. Trazer por cima seria apagar trabalho.
 */

/**
 * Doadoras da OPU que ainda não estão na FIV.
 *
 * O id do animal manda; o identificador é reserva para a linha lançada à mão,
 * que tem o brinco digitado mas nunca foi ligada ao cadastro.
 */
export const mesmaDoadora = (naFiv, naOpu) => Boolean(
  (naFiv?.donor_id && naFiv.donor_id === naOpu?.animal_id) ||
  (naFiv?.donor_identifier && naFiv.donor_identifier === naOpu?.animal_identifier)
);

export const doadorasQueFaltam = (naFiv = [], naOpu = []) =>
  naOpu.filter(o => !naFiv.some(d => mesmaDoadora(d, o)));

/**
 * Embriões da FIV que não estão listados na TE.
 *
 * Casa por id e TAMBÉM por número: a TE reconstrói linhas sintéticas para
 * transferências cujo embrião sumiu da tabela, e essas só têm o número. Sem
 * olhar o número, importar duplicaria a linha da transferência.
 */
export const embrioesQueFaltam = (naTe = [], naFiv = []) => {
  const ids  = new Set(naTe.map(e => e.id).filter(Boolean));
  // O descongelado do estoque traz o número de OUTRO serviço: casar por ele
  // esconderia o embrião daqui que tem o mesmo número. Esse casa só pelo id.
  const nums = new Set(naTe.filter(e => !e._doEstoque)
    .map(e => e.service_number).filter(n => n !== null && n !== undefined));
  return naFiv.filter(e =>
    !ids.has(e.id) &&
    (e._doEstoque ||
      !(e.service_number !== null && e.service_number !== undefined && nums.has(e.service_number))));
};

/**
 * Transferências aprovadas na TE que ainda não têm linha de diagnóstico.
 *
 * `transfer_id` é o que liga as duas etapas. Transferência sem id não entra:
 * sem chave não há como saber se já tem diagnóstico, e importar às cegas
 * duplicaria a receptora a cada clique.
 */
export const transferenciasSemDiagnostico = (checks = [], transfers = []) => {
  const jaTem = new Set(checks.map(c => c.transfer_id).filter(Boolean));
  return transfers.filter(t => t.id && !jaTem.has(t.id));
};

/**
 * A lista da TE, reconciliada com o que a FIV tem AGORA.
 *
 * `embrioesQueFaltam` acrescenta o que não está aqui. Esta função cuida do
 * outro lado — o que está aqui e MUDOU lá — e existe porque as abas do
 * workflow são todas montadas juntas: a TE lê os embriões uma vez, quando o
 * serviço abre, e nunca mais. Numerar na FIV depois disso não chegava nela.
 *
 * Isso não era só tela desatualizada. O save da TE regravava a numeração da
 * lista inteira, então a cópia velha voltava por cima da numeração nova a cada
 * "Salvar" — a FIV era corrigida e desfeita no mesmo clique.
 *
 * DUAS COISAS NÃO SÃO SUBSTITUÍDAS, e é o que separa isto de um recarregar:
 *
 *   1. O número que alguém digitou AQUI e ainda não gravou (`editados`). Quem
 *      digitou está com a palheta na mão; o banco não sabe disso ainda.
 *   2. A linha que a FIV não tem mais. Ela pode ser a linha sintética de uma
 *      transferência cujo embrião sumiu da tabela — a única prova que restou
 *      dela. Sumir com a linha aqui apagaria a transferência da tela.
 */
export const embrioesSincronizados = (naTe = [], naFiv = [], editados = new Set()) => {
  const porId = new Map(naFiv.map(e => [e.id, e]));
  const atuais = naTe.map(e => {
    const doBanco = porId.get(e.id);
    if (!doBanco) return e;
    return editados.has(e.id)
      ? { ...doBanco, service_number: e.service_number }
      : doBanco;
  });
  return [...atuais, ...embrioesQueFaltam(atuais, naFiv)];
};

/**
 * A FIV refeita a partir da OPU: "Substituir pela OPU".
 *
 * UMA LINHA POR LINHA DA OPU, na ordem dela. Doadora repetida na OPU (a mesma
 * vaca com dois touros, ou lançada duas vezes) vira duas linhas aqui; se a
 * equipe quiser juntar, junta depois. Espelhar é o que torna o botão
 * previsível.
 *
 * O ID DA LINHA É REAPROVEITADO quando a doadora já estava na FIV: cada linha
 * existente serve a UMA linha da OPU, a primeira que casar. É o id que liga os
 * embriões à doadora (`embryos.fiv_donor_id`); mantido, o banco reconcilia os
 * embriões pelas contagens em vez de apagar a linha e soltar os embriões dela.
 *
 * Todo o resto da FIV — clivados, estágios, destinos — volta a zero. É isso
 * que a pessoa pediu: descartar o que preencheu.
 *
 * Devolve também `saem`: as linhas da FIV que não sobraram (as que só existiam
 * aqui, e as repetições que a OPU não tem), para a confirmação dizer o que vai
 * junto.
 */
export const fivSubstituidaPelaOpu = (naFiv = [], naOpu = [], novoId) => {
  const usadas = new Set();
  const linhas = naOpu.map(o => {
    const existente = naFiv.find(d => d.id && !usadas.has(d.id) && mesmaDoadora(d, o));
    if (existente) usadas.add(existente.id);
    return {
      id: existente?.id || novoId(),
      _nova: !existente,
      donor_id: o.animal_id || null,
      donor_identifier: o.animal_identifier || '',
      bull: o.bull || o.bull_name || '',
      oocytes_received: o.oocytes_recovered || 0,
      viable_oocytes: o.oocytes_viable || 0,
      from_opu: true,
    };
  });
  const saem = naFiv.filter(d => !usadas.has(d.id) && (d.donor_id || d.donor_identifier));
  return { linhas, saem };
};
